import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth } from "@/App";
import { cvApi } from "@/lib/cv";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import ResumeRenderer from "@/components/cv/ResumeRenderer";
import ResumeReviewModal from "@/components/cv/ResumeReviewModal";
import BackBar from "@/components/BackBar";
import "@/components/cv/resume.css";
import {
  Save, Download, Sparkles, Plus, Trash2, ChevronDown, ChevronUp,
  Loader2, ArrowLeft, FileText, Lock, Crown, Wand2, Eye, ListChecks,
  Palette, Edit3, Unlock, Languages,
} from "lucide-react";

const newId = () => Math.random().toString(36).slice(2, 12);
const MOBILE_TABS = [
  { key: "preview",   ar: "معاينة",  en: "Preview",   Icon: Eye },
  { key: "edit",      ar: "تعديل",   en: "Edit",      Icon: Edit3 },
  { key: "templates", ar: "القوالب", en: "Templates", Icon: Palette },
  { key: "ai",        ar: "AI",      en: "AI",        Icon: Wand2 },
];

const SECTIONS = [
  { key: "contact",        ar: "البيانات الشخصية",  en: "Contact" },
  { key: "summary",        ar: "الملخص المهني",      en: "Summary" },
  { key: "experience",     ar: "الخبرات",            en: "Experience" },
  { key: "education",      ar: "التعليم",            en: "Education" },
  { key: "skills",         ar: "المهارات",           en: "Skills" },
  { key: "languages",      ar: "اللغات",             en: "Languages" },
  { key: "certifications", ar: "الشهادات",           en: "Certifications" },
  { key: "achievements",   ar: "الإنجازات",          en: "Achievements" },
];

export default function ResumeBuilder() {
  const { id } = useParams();
  const { t, language } = useLanguage();
  const { token, user } = useAuth();
  // Paid-status check — true once the user has unlocked the Career Success package.
  // Until Stripe is wired, defaults to false → the small paywall popup is shown on download.
  const cvPaid = !!(user && (user.cv_premium_paid === true || user.cv_premium_paid === "true"));
  const [showPaywall, setShowPaywall] = useState(false);
  const [translating, setTranslating] = useState(false);
  const navigate = useNavigate();
  const [resume, setResume] = useState(null);
  // Resume content language — independent of UI language. Persisted with the resume.
  const resumeLang = (resume && resume.language) || (language === "en" ? "en" : "ar");

  /**
   * Toggle the resume CONTENT (not the UI) between Arabic and US business English.
   * Backed by /cv/translate which preserves the JSON shape — only the strings change.
   * The live preview AND the downloaded PDF re-render in the chosen language.
   */
  const toggleResumeLanguage = async () => {
    if (translating || !resume) return;
    const target = resumeLang === "ar" ? "en" : "ar";
    setTranslating(true);
    try {
      const res = await cvApi.translate({
        resume_data: resume.data || {},
        target_language: target,
        source_language: resumeLang,
      }, token);
      const next = { ...resume, data: res.data || resume.data, language: target };
      setResume(next);
      try { await cvApi.update(id, next, token); } catch (_) { /* best-effort persist */ }
    } catch (e) {
      toast.error(t("تعذّر الترجمة الآن. حاول مرة أخرى.", "Translation failed. Please try again."));
    } finally {
      setTranslating(false);
    }
  };
  const [templates, setTemplates] = useState({ free: [], categories: [] });
  const [professions, setProfessions] = useState([]);
  const [sample, setSample] = useState(null);  // sample resume — for empty-template thumbnails
  const [showReview, setShowReview] = useState(false);
  const [open, setOpen] = useState({ contact: true, summary: true, experience: true });
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState("");
  const [mobileTab, setMobileTab] = useState("edit");
  const [previewAll, setPreviewAll] = useState(
    typeof window !== "undefined" && localStorage.getItem("cv_preview_all") === "1"
  );
  const saveTimer = useRef(null);

  const togglePreviewAll = () => {
    const next = !previewAll;
    setPreviewAll(next);
    localStorage.setItem("cv_preview_all", next ? "1" : "0");
    toast.success(next ? t("تم فتح كل القوالب للمعاينة", "All templates unlocked for preview") : t("القوالب البريميوم مقفلة", "Premium templates re-locked"));
  };

  useEffect(() => {
    cvApi.templates().then(setTemplates).catch(() => {});
    cvApi.professions().then(setProfessions).catch(() => {});
    cvApi.sample(language === "ar" ? "ar" : "en").then(setSample).catch(() => {});
    if (!token) { navigate("/login"); return; }
    cvApi.get(id, token)
      .then(setResume)
      .catch(() => { toast.error(t("تعذّر تحميل السيرة", "Could not load resume")); navigate("/cv"); });
  }, [id, token, navigate, t, language]);

  // Auto-save (debounced) — Unlimited Updates feature.
  useEffect(() => {
    if (!resume) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        setSaving(true);
        await cvApi.update(id, {
          title: resume.title, profession: resume.profession,
          template: resume.template, data: resume.data,
        }, token);
      } catch { /* swallow; user will retry */ } finally { setSaving(false); }
    }, 900);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [resume, id, token]);

  if (!resume) {
    return (
      <DashboardLayout title={t("تحميل السيرة…", "Loading resume…")}>
        <div className="flex items-center justify-center p-20"><Loader2 className="animate-spin text-yellow-500" size={32} /></div>
      </DashboardLayout>
    );
  }

  const allTemplates = [...templates.free, ...templates.categories.flatMap(c => c.templates.filter(t => t.premium))];
  const update = (path, value) => {
    const next = JSON.parse(JSON.stringify(resume));
    const keys = path.split(".");
    let ref = next;
    for (let i = 0; i < keys.length - 1; i++) ref = ref[keys[i]];
    ref[keys[keys.length - 1]] = value;
    setResume(next);
  };
  const updateArrayItem = (section, idx, field, value) => {
    const arr = [...resume.data[section]];
    arr[idx] = { ...arr[idx], [field]: value };
    update(`data.${section}`, arr);
  };
  const addArrayItem = (section, item) => update(`data.${section}`, [...resume.data[section], { id: newId(), ...item }]);
  const removeArrayItem = (section, idx) => update(`data.${section}`, resume.data[section].filter((_, i) => i !== idx));

  // ─── AI helpers ───
  const runAI = async (kind, payload, applyFn) => {
    setAiLoading(kind);
    try {
      const body = { profession: resume.profession, language: language === "ar" ? "ar" : "en", ...payload };
      const fn = {
        summary:      cvApi.aiSummary,
        bullets:      cvApi.aiBullets,
        skills:       cvApi.aiSkills,
        achievements: cvApi.aiAchievements,
      }[kind];
      const res = await fn(body, token);
      applyFn(res);
      toast.success(t("تم التوليد بالذكاء الاصطناعي", "AI generated"));
    } catch { toast.error(t("فشل التوليد. حاول مجدداً.", "AI generation failed.")); }
    finally { setAiLoading(""); }
  };

  const isPremium = (slug) => {
    const tpl = allTemplates.find((tp) => tp.slug === slug);
    return tpl && tpl.premium;
  };

  const handlePrint = () => {
    // Paid users go straight to the AI Review → download flow. Free users see a
    // small paywall popup pointing them to the Career Success package.
    if (cvPaid) {
      setShowReview(true);
    } else {
      setShowPaywall(true);
    }
  };

  // Helper: detect whether the user's resume is still mostly empty (used for thumbnails).
  const isEmpty = (() => {
    if (!resume) return true;
    const d = resume.data || {};
    const fields = [d.contact?.full_name, d.summary, ...(d.experience || []).map(x => x.role)];
    return !fields.some((f) => (f || "").trim());
  })();
  const thumbData = isEmpty && sample ? sample : (resume ? resume.data : null);

  const inputCls = "bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white w-full text-sm focus:border-yellow-500 outline-none";
  const labelCls = "text-xs text-slate-400 mb-1 block";

  return (
    <DashboardLayout title={resume.title || t("سيرة ذاتية", "Resume")}>
      <BackBar to="/cv" label_ar="مركز السيرة الذاتية" label_en="Resume Center"
        crumbs={[{ to: "/dashboard", ar: "الرئيسية", en: "Home" }, { to: "/cv", ar: "السيرة الذاتية", en: "Resume Center" }, { ar: resume.title || "تعديل", en: resume.title || "Edit" }]} />
      {/* Mobile tab bar — visible only on small screens */}
      <div className="lg:hidden mb-3 flex gap-1 bg-slate-900/60 rounded-2xl p-1" data-testid="cv-mobile-tabs">
        {MOBILE_TABS.map((tab) => (
          <button key={tab.key} onClick={() => setMobileTab(tab.key)}
            className={`flex-1 flex flex-col items-center gap-0.5 py-2 rounded-xl text-[11px] ${mobileTab===tab.key ? "bg-yellow-500 text-black" : "text-slate-300"}`}
            data-testid={`mobile-tab-${tab.key}`}>
            <tab.Icon size={14} />{language === "ar" ? tab.ar : tab.en}
          </button>
        ))}
      </div>
      <div className="grid lg:grid-cols-[minmax(420px,1fr)_minmax(580px,1.4fr)] gap-6" data-testid="resume-builder">

        {/* LEFT — FORM (hidden on mobile when not on edit/templates/ai) */}
        <div className={`space-y-4 ${["edit","templates","ai"].includes(mobileTab) ? "" : "hidden lg:block"}`}>
          {/* Topbar */}
          <div className="glass-card rounded-2xl p-4 flex flex-wrap items-center gap-3" data-testid="builder-topbar">
            <Button onClick={() => navigate("/cv")} variant="outline" size="sm" className="border-slate-700 text-slate-300">
              <ArrowLeft size={14} className="ml-1" />{t("رجوع", "Back")}
            </Button>
            <Input value={resume.title} onChange={(e) => update("title", e.target.value)}
              className="flex-1 bg-slate-900 border-slate-700 text-white" placeholder={t("عنوان السيرة", "Resume title")}
              data-testid="builder-title-input" />
            <div className="text-xs text-slate-400 flex items-center gap-1">
              {saving ? <><Loader2 size={12} className="animate-spin" />{t("جارٍ الحفظ…", "Saving…")}</> : <>✓ {t("تم الحفظ", "Saved")}</>}
            </div>
            {/* Inline resume-language toggle. Unlike the global UI-language toggle this one
                actually re-writes `resume.data` via the existing /cv/translate endpoint, so
                the live preview AND the downloaded PDF reflect the chosen language. */}
            <Button
              onClick={toggleResumeLanguage}
              disabled={translating}
              variant="outline"
              size="sm"
              className="border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10"
              title={resumeLang === "ar" ? "Translate to English" : "ترجم للعربية"}
              data-testid="resume-lang-toggle"
            >
              {translating ? <Loader2 size={14} className="animate-spin ml-1" /> : <Languages size={14} className="ml-1" />}
              {resumeLang === "ar" ? "English" : "العربية"}
            </Button>
            <Button onClick={handlePrint} className="btn-primary" data-testid="builder-print-btn">
              <Download size={14} className="ml-2" />{t("تحميل PDF", "Download PDF")}
            </Button>
          </div>

          {/* Profession + Template */}
          <div className="glass-card rounded-2xl p-4 space-y-3">
            <div>
              <label className={labelCls}>{t("المهنة", "Profession")}</label>
              <select value={resume.profession} onChange={(e) => update("profession", e.target.value)}
                className={inputCls} data-testid="builder-profession">
                <option value="">{t("اختر مهنتك", "Select profession")}</option>
                {professions.map((p) => (
                  <option key={p.slug} value={p.slug}>{language === "ar" ? p.ar : p.en}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>{t("القالب", "Template")} <span className="text-slate-500">(55+ {t("قالب", "templates")})</span></label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[520px] overflow-y-auto pr-1" data-testid="builder-template-grid">
                {[...templates.free, ...templates.categories.flatMap(c => c.templates.filter(t => t.premium))].map((tpl) => {
                  const selected = resume.template === tpl.slug;
                  // Templates are previewable by everyone — the lock is removed.
                  // Premium templates still show their badge, but can be selected freely.
                  return (
                    <button key={tpl.slug}
                      onClick={() => update("template", tpl.slug)}
                      className={`group relative rounded-xl border overflow-hidden transition ${
                        selected ? "border-yellow-500 ring-2 ring-yellow-500/40"
                                 : "border-slate-700 hover:border-slate-500"
                      } bg-white`}
                      data-testid={`tpl-${tpl.slug}`}
                      title={(language === "ar" ? tpl.name_ar : tpl.name_en) + (tpl.premium ? " (Premium)" : "")}
                    >
                      <div className="preview-frame-compact" style={{ background: "transparent", padding: 0, height: 180, overflow: "hidden", pointerEvents: "none" }}>
                        <ResumeRenderer template={tpl.slug} data={thumbData || resume.data} allTemplates={allTemplates} />
                      </div>
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent text-white text-[10px] px-2 py-1.5 flex items-center justify-between">
                        <span className="truncate">{language === "ar" ? tpl.name_ar : tpl.name_en}</span>
                        {tpl.premium && <Crown size={10} className="text-yellow-400 flex-shrink-0" />}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center justify-between text-[11px] mt-2">
                {isPremium(resume.template) && <p className="text-yellow-400"><Crown size={11} className="inline ml-1" />{t("قالب بريميوم", "Premium template")}</p>}
              </div>
            </div>
          </div>

          {/* Customize panel: theme, skill style, photo */}
          <div className="glass-card rounded-2xl p-4 space-y-3" data-testid="builder-customize">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Palette size={14} className="text-yellow-500" />{t("التخصيص", "Customize")}</h3>

            {/* Theme picker — 10 swatches */}
            <div>
              <label className={labelCls}>{t("اللون", "Color theme")}</label>
              <div className="flex flex-wrap gap-2" data-testid="builder-themes">
                {[
                  ["navy", "#1E3A8A"], ["blue", "#2563EB"], ["teal", "#0D9488"], ["green", "#16A34A"], ["purple", "#7C3AED"],
                  ["red", "#DC2626"], ["orange", "#EA580C"], ["gray", "#475569"], ["black", "#111827"], ["gold", "#B45309"],
                ].map(([slug, color]) => {
                  const active = (resume.data?.options?.theme || allTemplates.find(t => t.slug === resume.template)?.theme || "navy") === slug;
                  return (
                    <button key={slug}
                      onClick={() => update("data.options", { ...(resume.data.options || {}), theme: slug })}
                      className={`w-8 h-8 rounded-full border-2 transition ${active ? "border-yellow-400 scale-110" : "border-slate-700 hover:border-slate-500"}`}
                      style={{ background: color }}
                      title={slug}
                      data-testid={`theme-${slug}`}
                    />
                  );
                })}
              </div>
            </div>

            {/* Skill style picker */}
            <div>
              <label className={labelCls}>{t("نمط عرض المهارات", "Skills display style")}</label>
              <div className="grid grid-cols-3 gap-1.5" data-testid="builder-skill-styles">
                {[
                  ["bullets", t("نقاط",  "Bullets")],
                  ["tags",    t("وسوم",   "Tags")],
                  ["bars",    t("أشرطة",  "Bars")],
                  ["dots",    t("نقاط دائرية", "Dots")],
                  ["percent", t("نسبة",   "Percent")],
                  ["stars",   t("نجوم",   "Stars")],
                ].map(([slug, label]) => {
                  const active = (resume.data?.options?.skill_style || allTemplates.find(t => t.slug === resume.template)?.options?.skill_style || "tags") === slug;
                  return (
                    <button key={slug}
                      onClick={() => update("data.options", { ...(resume.data.options || {}), skill_style: slug })}
                      className={`text-xs px-2 py-1.5 rounded border ${active ? "border-yellow-500 bg-yellow-500/10 text-yellow-200" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}
                      data-testid={`skill-style-${slug}`}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Photo */}
            <div>
              <label className={labelCls}>{t("الصورة الشخصية (اختياري)", "Profile photo (optional)")}</label>
              <div className="flex items-center gap-2">
                {resume.data.contact?.photo && (
                  <div className="w-12 h-12 rounded-full bg-cover bg-center border border-slate-700" style={{ backgroundImage: `url(${resume.data.contact.photo})` }} />
                )}
                <label className="flex-1 cursor-pointer text-xs bg-slate-900 border border-slate-700 text-slate-300 rounded-lg px-3 py-2 hover:border-slate-500 inline-flex items-center justify-center gap-1" data-testid="photo-upload">
                  📷 {resume.data.contact?.photo ? t("غيّر الصورة", "Change photo") : t("ارفع صورة", "Upload photo")}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    if (f.size > 1.5 * 1024 * 1024) { toast.error(t("الصورة كبيرة جداً (الحد 1.5MB)", "Image too large (max 1.5MB)")); return; }
                    const reader = new FileReader();
                    reader.onload = () => update("data.contact.photo", reader.result);
                    reader.readAsDataURL(f);
                  }} />
                </label>
                {resume.data.contact?.photo && (
                  <button onClick={() => update("data.contact.photo", "")} className="text-xs text-red-400 hover:text-red-300 px-2" data-testid="photo-remove">
                    {t("حذف", "Remove")}
                  </button>
                )}
              </div>
              <p className="text-[10px] text-slate-500 mt-1">{t("ملاحظة: غالباً لا تُستخدم الصورة في السير الأمريكية. تُستخدم لوظائف خارج الولايات المتحدة أو مهن الإبداع/الخدمة.", "Note: Not standard on US resumes. Use it for roles outside the US or creative/service industries.")}</p>
            </div>
          </div>
          {SECTIONS.map((sec) => (
            <SectionBlock key={sec.key} sec={sec} t={t} language={language} open={open[sec.key]}
              onToggle={() => setOpen({ ...open, [sec.key]: !open[sec.key] })}>
              {sec.key === "contact" && (
                <div className="grid grid-cols-2 gap-3">
                  {["full_name","title","email","phone","location","linkedin","website"].map((f) => (
                    <div key={f} className={f === "title" ? "col-span-2" : ""}>
                      <label className={labelCls}>{t({full_name:"الاسم الكامل",title:"المسمى الوظيفي",email:"البريد الإلكتروني",phone:"الهاتف",location:"المكان (مدينة، ولاية)",linkedin:"LinkedIn",website:"الموقع"}[f], {full_name:"Full Name",title:"Professional Title",email:"Email",phone:"Phone",location:"Location (City, State)",linkedin:"LinkedIn",website:"Website"}[f])}</label>
                      <input value={resume.data.contact[f]} onChange={(e) => update(`data.contact.${f}`, e.target.value)} className={inputCls} data-testid={`contact-${f}`} />
                    </div>
                  ))}
                </div>
              )}

              {sec.key === "summary" && (
                <div className="space-y-2">
                  <textarea value={resume.data.summary} onChange={(e) => update("data.summary", e.target.value)}
                    rows={4} className={inputCls} placeholder={t("3-4 جمل تصف خبرتك ومهاراتك الأساسية…", "3-4 sentences describing your experience and core skills…")}
                    data-testid="summary-input" />
                  <Button size="sm" onClick={() => runAI("summary", { summary_context: resume.data.summary }, (r) => update("data.summary", r.summary))}
                    disabled={!resume.profession || aiLoading === "summary"} className="btn-primary"
                    data-testid="ai-summary-btn">
                    {aiLoading === "summary" ? <Loader2 size={12} className="animate-spin ml-1" /> : <Wand2 size={12} className="ml-1" />}
                    {t("اكتب لي ملخصاً", "Write me a summary")}
                  </Button>
                  {!resume.profession && <p className="text-[10px] text-slate-500">{t("اختر مهنتك أولاً لتفعيل المساعد الذكي.", "Pick your profession first to enable AI.")}</p>}
                </div>
              )}

              {sec.key === "experience" && (
                <div className="space-y-3">
                  {resume.data.experience.map((x, idx) => (
                    <div key={x.id} className="bg-slate-900/50 rounded-lg p-3 space-y-2" data-testid={`exp-${idx}`}>
                      <div className="grid grid-cols-2 gap-2">
                        <input value={x.role} onChange={(e) => updateArrayItem("experience", idx, "role", e.target.value)} placeholder={t("المسمى الوظيفي", "Role")} className={inputCls} />
                        <input value={x.company} onChange={(e) => updateArrayItem("experience", idx, "company", e.target.value)} placeholder={t("الشركة", "Company")} className={inputCls} />
                        <input value={x.start_date} onChange={(e) => updateArrayItem("experience", idx, "start_date", e.target.value)} placeholder="Jan 2022" className={inputCls} />
                        <input value={x.end_date} onChange={(e) => updateArrayItem("experience", idx, "end_date", e.target.value)} placeholder="Present" className={inputCls} />
                      </div>
                      <textarea value={(x.bullets || []).join("\n")}
                        onChange={(e) => updateArrayItem("experience", idx, "bullets", e.target.value.split("\n"))}
                        rows={4} className={inputCls} placeholder={t("نقطة لكل سطر — ابدأ بفعل قوي.", "One bullet per line — start with a strong verb.")} />
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline"
                          onClick={() => runAI("bullets", { role: x.role, company: x.company, achievements_context: x.bullets?.join("; ") }, (r) => updateArrayItem("experience", idx, "bullets", r.bullets))}
                          disabled={!resume.profession || aiLoading === "bullets" || !x.role}
                          className="border-yellow-500/40 text-yellow-400"
                          data-testid={`ai-bullets-${idx}`}>
                          {aiLoading === "bullets" ? <Loader2 size={11} className="animate-spin ml-1" /> : <Wand2 size={11} className="ml-1" />}
                          {t("اكتب لي نقاطاً", "AI bullets")}
                        </Button>
                        <button onClick={() => removeArrayItem("experience", idx)} className="text-red-400 hover:text-red-300 p-1.5 rounded bg-slate-800">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  ))}
                  <Button size="sm" onClick={() => addArrayItem("experience", { role:"", company:"", location:"", start_date:"", end_date:"", bullets:[] })}
                    className="btn-primary" data-testid="add-experience">
                    <Plus size={12} className="ml-1" />{t("إضافة خبرة", "Add experience")}
                  </Button>
                </div>
              )}

              {sec.key === "education" && (
                <div className="space-y-3">
                  {resume.data.education.map((e, idx) => (
                    <div key={e.id} className="bg-slate-900/50 rounded-lg p-3 space-y-2" data-testid={`edu-${idx}`}>
                      <div className="grid grid-cols-2 gap-2">
                        <input value={e.degree} onChange={(ev) => updateArrayItem("education", idx, "degree", ev.target.value)} placeholder={t("الدرجة", "Degree")} className={inputCls} />
                        <input value={e.field} onChange={(ev) => updateArrayItem("education", idx, "field", ev.target.value)} placeholder={t("التخصص", "Field")} className={inputCls} />
                        <input value={e.school} onChange={(ev) => updateArrayItem("education", idx, "school", ev.target.value)} placeholder={t("المدرسة/الجامعة", "School / University")} className={inputCls + " col-span-2"} />
                        <input value={e.start_date} onChange={(ev) => updateArrayItem("education", idx, "start_date", ev.target.value)} placeholder="2018" className={inputCls} />
                        <input value={e.end_date} onChange={(ev) => updateArrayItem("education", idx, "end_date", ev.target.value)} placeholder="2022" className={inputCls} />
                      </div>
                      <button onClick={() => removeArrayItem("education", idx)} className="text-red-400 hover:text-red-300 p-1.5 rounded bg-slate-800 w-fit">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                  <Button size="sm" onClick={() => addArrayItem("education", { school:"", degree:"", field:"", start_date:"", end_date:"", gpa:"", notes:"" })}
                    className="btn-primary" data-testid="add-education">
                    <Plus size={12} className="ml-1" />{t("إضافة تعليم", "Add education")}
                  </Button>
                </div>
              )}

              {sec.key === "skills" && (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2" data-testid="skills-tags">
                    {resume.data.skills.map((s, idx) => (
                      <span key={s.id} className="bg-slate-800 text-white text-xs px-3 py-1 rounded-full flex items-center gap-2">
                        {s.name || "—"}
                        <button onClick={() => removeArrayItem("skills", idx)} className="text-red-400">×</button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input id="skill-input" placeholder={t("أضف مهارة واضغط Enter", "Add a skill and press Enter")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && e.target.value.trim()) {
                          addArrayItem("skills", { name: e.target.value.trim() });
                          e.target.value = "";
                        }
                      }} className={inputCls} data-testid="skill-input" />
                    <Button size="sm" onClick={() => runAI("skills", {}, (r) => update("data.skills", r.skills.map((n) => ({ id: newId(), name: n }))))}
                      disabled={!resume.profession || aiLoading === "skills"} className="btn-primary" data-testid="ai-skills-btn">
                      {aiLoading === "skills" ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                    </Button>
                  </div>
                </div>
              )}

              {sec.key === "languages" && (
                <div className="space-y-2">
                  {resume.data.languages.map((l, idx) => (
                    <div key={l.id} className="flex gap-2">
                      <input value={l.name} onChange={(e) => updateArrayItem("languages", idx, "name", e.target.value)} placeholder={t("اللغة", "Language")} className={inputCls} />
                      <select value={l.proficiency} onChange={(e) => updateArrayItem("languages", idx, "proficiency", e.target.value)} className={inputCls}>
                        <option value="">—</option>
                        <option>Native</option><option>Fluent</option><option>Professional</option><option>Conversational</option><option>Basic</option>
                      </select>
                      <button onClick={() => removeArrayItem("languages", idx)} className="text-red-400 px-2"><Trash2 size={13} /></button>
                    </div>
                  ))}
                  <Button size="sm" onClick={() => addArrayItem("languages", { name:"", proficiency:"" })} className="btn-primary">
                    <Plus size={12} className="ml-1" />{t("إضافة لغة", "Add language")}
                  </Button>
                </div>
              )}

              {sec.key === "certifications" && (
                <div className="space-y-2">
                  {resume.data.certifications.map((c, idx) => (
                    <div key={c.id} className="grid grid-cols-3 gap-2">
                      <input value={c.name} onChange={(e) => updateArrayItem("certifications", idx, "name", e.target.value)} placeholder={t("اسم الشهادة", "Certification")} className={inputCls} />
                      <input value={c.issuer} onChange={(e) => updateArrayItem("certifications", idx, "issuer", e.target.value)} placeholder={t("الجهة", "Issuer")} className={inputCls} />
                      <div className="flex gap-1">
                        <input value={c.year} onChange={(e) => updateArrayItem("certifications", idx, "year", e.target.value)} placeholder="2024" className={inputCls} />
                        <button onClick={() => removeArrayItem("certifications", idx)} className="text-red-400 px-2"><Trash2 size={13} /></button>
                      </div>
                    </div>
                  ))}
                  <Button size="sm" onClick={() => addArrayItem("certifications", { name:"", issuer:"", year:"" })} className="btn-primary">
                    <Plus size={12} className="ml-1" />{t("إضافة شهادة", "Add certification")}
                  </Button>
                </div>
              )}

              {sec.key === "achievements" && (
                <div className="space-y-2">
                  <textarea value={resume.data.achievements.join("\n")}
                    onChange={(e) => update("data.achievements", e.target.value.split("\n"))}
                    rows={5} className={inputCls} placeholder={t("سطر لكل إنجاز…", "One achievement per line…")} />
                  <Button size="sm" onClick={() => runAI("achievements", { achievements_context: resume.data.achievements.join("; ") }, (r) => update("data.achievements", r.achievements))}
                    disabled={!resume.profession || aiLoading === "achievements"} className="btn-primary" data-testid="ai-ach-btn">
                    {aiLoading === "achievements" ? <Loader2 size={12} className="animate-spin ml-1" /> : <Wand2 size={12} className="ml-1" />}
                    {t("اقترح إنجازات", "Suggest achievements")}
                  </Button>
                </div>
              )}
            </SectionBlock>
          ))}
        </div>

        {/* RIGHT — LIVE PREVIEW */}
        <div className={`lg:sticky lg:top-4 lg:self-start space-y-3 ${mobileTab === "preview" ? "" : "hidden lg:block"}`} data-testid="resume-preview-pane">
          <div className="glass-card rounded-2xl p-3 flex items-center justify-between gap-2 flex-wrap">
            <p className="text-xs text-slate-400 flex items-center gap-1"><Eye size={12} />{t("معاينة حية (8.5×11)", "Live preview (8.5×11)")}</p>
            <Badge className="bg-slate-800 text-slate-300 text-[10px]">{resume.template}</Badge>
          </div>
          <div className="preview-frame relative">
            <ResumeRenderer template={resume.template} data={resume.data} allTemplates={allTemplates} />
            {/* Subtle watermark — shown ONLY on the live preview, never on the final PDF (the
                downloaded PDF goes through window.print() AFTER the user has paid, so this
                overlay won't be present then). */}
            {!cvPaid && (
              <div
                className="absolute inset-0 pointer-events-none flex items-center justify-center print:hidden"
                aria-hidden="true"
                data-testid="cv-preview-watermark"
              >
                <span
                  className="font-bold tracking-[0.6em] select-none uppercase"
                  style={{
                    color: "rgba(255,255,255,0.07)",
                    fontSize: "min(11vw, 6rem)",
                    transform: "rotate(-22deg)",
                    whiteSpace: "nowrap",
                    textShadow: "0 0 1px rgba(0,0,0,0.06)",
                  }}
                >
                  PREVIEW · ARAB SANAD · {(user?.name || "").toUpperCase()}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Paywall popup — small, focused message for non-paid users hitting the Download PDF button. */}
      {showPaywall && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowPaywall(false)} data-testid="cv-paywall-overlay">
          <div className="glass-card rounded-2xl p-6 max-w-md w-full border border-yellow-500/40 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="cv-paywall-popup">
            <div className="flex items-center gap-2 text-yellow-400">
              <Crown size={18} />
              <h3 className="font-bold text-lg text-white">{t("تنزيل PDF", "Download PDF")}</h3>
            </div>
            <p className="text-slate-200 text-sm leading-relaxed">
              {t(
                "لتحميل السيرة الذاتية بصيغة PDF، يرجى تفعيل باقة النجاح المهني بسعر العرض الحالي $4.99 لفترة محدودة.",
                "To download your resume as PDF, please activate the Career Success Package at the current limited-time offer of $4.99."
              )}
            </p>
            <div className="flex gap-2 justify-end pt-2">
              <button onClick={() => setShowPaywall(false)} className="px-4 py-2 rounded-xl text-sm text-slate-300 hover:bg-slate-800" data-testid="cv-paywall-close">
                {t("إغلاق", "Close")}
              </button>
              <button onClick={() => { setShowPaywall(false); navigate("/cv/premium"); }} className="btn-primary text-sm" data-testid="cv-paywall-subscribe">
                {t("اشترك الآن", "Subscribe now")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReview && (
        <ResumeReviewModal
          resume={resume}
          allTemplates={allTemplates}
          token={token}
          onClose={() => setShowReview(false)}
          onDownload={(translatedData) => {
            // If the user picked the English version, temporarily swap data, print, restore.
            if (translatedData) {
              const original = resume.data;
              setResume({ ...resume, data: translatedData });
              setTimeout(() => {
                window.print();
                setResume((r) => r ? { ...r, data: original } : r);
              }, 250);
            } else {
              window.print();
            }
            setShowReview(false);
          }}
        />
      )}
    </DashboardLayout>
  );
}

function SectionBlock({ sec, t, language, open, onToggle, children }) {
  return (
    <div className="glass-card rounded-2xl" data-testid={`section-${sec.key}`}>
      <button onClick={onToggle} className="w-full flex items-center justify-between p-4 text-right" data-testid={`section-toggle-${sec.key}`}>
        <span className="font-semibold text-white flex items-center gap-2"><ListChecks size={16} className="text-yellow-500" />{language === "ar" ? sec.ar : sec.en}</span>
        {open ? <ChevronUp className="text-slate-400" size={18} /> : <ChevronDown className="text-slate-400" size={18} />}
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}
